/**
 * Le Tribunal SmartLawyer — Page d'accueil v3 (Framer Motion)
 * Route : /tribunal
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence, MotionConfig, useMotionValue, useSpring, useTransform, type Variants } from 'framer-motion';
import { getIdToken } from '@/lib/firebase';
import { ROLE_AVATARS, TrialAvatar } from './Courtroom';
import { CourtHero } from './CourtHero';
import { FamousTrial } from './FamousTrial';
import { VibeLegal } from './VibeLegal';
import { useTranslation, Trans } from 'react-i18next';
import { getCurrentLanguage, getDateLocale } from '@/i18n';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Trial {
  id: string;
  title: string;
  headline: string;
  case_type: string;
  jurisdiction: string;
  difficulty: string;
  status: 'open' | 'in_progress' | 'completed';
  prosecution_name: string | null;
  defense_name: string | null;
  current_phase: string;
  created_at: string;
  completed_at: string | null;
  verdict?: { verdict: string; winner: string } | null;
}

// ── Config visuelle ────────────────────────────────────────────────────────────

/** Teintes claires dérivées de la charte SmartLawyer (fond #fff, bordures #e3d8c3, accent #a8742a). */
const TYPE_CONFIG: Record<string, { gradient: string; border: string; glow: string; accent: string; labelKey: string; icon: string }> = {
  'pénal':      { gradient: 'from-[#f6e9e3] to-white',     border: '#e2c3b5', glow: 'rgba(155,58,46,0.08)',  accent: '#9b3a2e', labelKey: 'tribunal.case_types.penal',      icon: '⚔️' },
  'civil':      { gradient: 'from-[#e9eef1] to-white',    border: '#b9c6cd', glow: 'rgba(61,90,115,0.08)', accent: '#3d5a73', labelKey: 'tribunal.case_types.civil',      icon: '📋' },
  'commercial': { gradient: 'from-[#f8eedb] to-white',  border: '#e9cf98', glow: 'rgba(168,116,42,0.08)', accent: '#a8742a', labelKey: 'tribunal.case_types.commercial', icon: '💼' },
  'social':     { gradient: 'from-[#e9f1ec] to-white', border: '#b8d0c3', glow: 'rgba(79,122,106,0.08)',  accent: '#4f7a6a', labelKey: 'tribunal.case_types.social',     icon: '🤝' },
};

/** Phases renvoyées par le backend (codes) — libellés via i18n `tribunal.phases.*`. */
const PHASE_CODES = ['waiting', 'opening', 'examination', 'evidence', 'closing', 'deliberation', 'verdict'];

/** Ajoute `lang=<langue courante>` à une URL /api/trials… (le backend choisit la langue des agents IA). */
function withLang(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}lang=${getCurrentLanguage()}`;
}

const DIFF: Record<string, { stars: string; color: string }> = {
  easy:   { stars: '★☆☆', color: '#4f7a6a' },
  medium: { stars: '★★☆', color: '#a8742a' },
  hard:   { stars: '★★★', color: '#9b3a2e' },
};

function fmt(d: string) {
  return new Date(d).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short' });
}

// ── Carte 3D tiltable ──────────────────────────────────────────────────────────

function TiltCard({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [6, -6]), { stiffness: 200, damping: 20 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-6, 6]), { stiffness: 200, damping: 20 });

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ rotateX, rotateY, transformStyle: 'preserve-3d', perspective: 800, ...style }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ── Siège joueur ───────────────────────────────────────────────────────────────

function PlayerSeat({ name, role }: { name: string | null; role: 'prosecution' | 'defense' }) {
  const { t } = useTranslation();
  const isPros = role === 'prosecution';
  const accent = isPros ? '#9b3a2e' : '#3d5a73';
  const label  = isPros ? t('tribunal.seats.prosecution') : t('tribunal.seats.defense');

  return (
    <div
      className="flex-1 min-w-0 flex items-center gap-2 px-2.5 py-2 rounded-xl"
      style={{
        background: name ? (isPros ? '#f6e9e3' : '#e9eef1') : '#fbf6ea',
        border: `1px solid ${name ? (isPros ? '#e2c3b5' : '#b9c6cd') : '#e3d8c3'}`,
      }}
    >
      <div className="relative flex-shrink-0">
        {name ? (
          <TrialAvatar role={role} size={28} alt="" />
        ) : (
          <motion.div
            className="w-7 h-7 rounded-full border-2 border-dashed flex items-center justify-center"
            style={{ borderColor: `${accent}66` }}
            animate={{ opacity: [0.55, 1, 0.55] }}
            transition={{ repeat: Infinity, duration: 2.4, ease: 'easeInOut' }}
          >
            <span className="text-[9px] font-bold" style={{ color: accent }}>?</span>
          </motion.div>
        )}
        {name && (
          <motion.span
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#5f8c7a] border-2 border-white"
          />
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold leading-tight" style={{ color: accent }}>{label}</div>
        <div className="text-[11px] text-muted-foreground truncate leading-tight">
          {name ?? t('tribunal.seats.free')}
        </div>
      </div>
    </div>
  );
}

// ── Carte affaire ─────────────────────────────────────────────────────────────

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 30, scale: 0.96 },
  visible: (i: number) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { delay: i * 0.07, type: 'spring', stiffness: 200, damping: 22 },
  }),
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
};

function TrialCard({ trial, index }: { trial: Trial; index: number }) {
  const { t } = useTranslation();
  const type   = TYPE_CONFIG[trial.case_type] ?? TYPE_CONFIG['civil'];
  const diff   = DIFF[trial.difficulty] ?? DIFF.medium;
  const isLive = trial.status === 'in_progress';
  const isOpen = trial.status === 'open';
  const canJoin = isOpen && (!trial.prosecution_name || !trial.defense_name);

  return (
    <motion.div custom={index} variants={cardVariants} initial="hidden" animate="visible" exit="exit" layout>
      <TiltCard className="h-full">
        <Link to={`/tribunal/${trial.id}`} className="block h-full">
          <motion.div
            whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(7,14,23,0.10)' }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="relative h-full flex flex-col bg-card rounded-2xl p-5"
            style={{ border: '1px solid #e3d8c3', boxShadow: '0 1px 2px rgba(7,14,23,0.04)' }}
          >
            {/* Ligne du haut : type · difficulté · action */}
            <div className="flex items-start justify-between gap-2 mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: type.accent }} />
                  <span className="text-xs font-semibold" style={{ color: type.accent }}>{t(type.labelKey)}</span>
                </span>
                <span className="text-xs tracking-tight" style={{ color: diff.color }}>{diff.stars}</span>
              </div>

              {isLive ? (
                <span
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg flex-shrink-0"
                  style={{ background: '#f6e9e3', border: '1px solid #e2c3b5' }}
                >
                  <motion.span
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                    className="w-1.5 h-1.5 rounded-full bg-[#b5523b]"
                  />
                  <span className="text-[10px] font-bold text-[#9b3a2e] uppercase tracking-wider">
                    {trial.current_phase && PHASE_CODES.includes(trial.current_phase) ? t(`tribunal.phases.${trial.current_phase}`) : t('tribunal.live')}
                  </span>
                </span>
              ) : canJoin ? (
                <motion.span
                  whileHover={{ scale: 1.05 }}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0 text-foreground"
                  style={{ background: '#ffffff', border: '1px solid #e3d8c3' }}
                >
                  {t('tribunal.join')}
                </motion.span>
              ) : null}
            </div>

            {/* Titre + résumé */}
            <h3 className="font-serif text-lg font-bold text-foreground leading-snug mb-2 line-clamp-2">
              {trial.title}
            </h3>
            <p className="text-sm leading-relaxed text-muted-foreground line-clamp-3 mb-5 flex-1">
              {trial.headline}
            </p>

            {/* Sièges */}
            <div className="flex items-center gap-2 mb-4">
              <PlayerSeat name={trial.prosecution_name} role="prosecution" />
              <span className="text-[10px] font-bold text-muted-foreground flex-shrink-0">VS</span>
              <PlayerSeat name={trial.defense_name} role="defense" />
            </div>

            {/* Verdict */}
            {trial.status === 'completed' && trial.verdict && (
              <div
                className="mb-4 text-center px-3 py-2 rounded-xl"
                style={{ background: '#fbf6ea', border: '1px solid #e3d8c3' }}
              >
                <span className="text-xs font-bold text-foreground/70 uppercase tracking-wider">
                  🔨 {trial.verdict.verdict}
                </span>
              </div>
            )}

            {/* Pied */}
            <div
              className="flex items-center justify-between pt-3.5"
              style={{ borderTop: '1px solid #e3d8c3' }}
            >
              <span className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <span aria-hidden>📅</span>{fmt(trial.created_at)}
              </span>
              <span className="text-[11px] font-semibold flex items-center gap-1" style={{ color: '#3d5a73' }}>
                {canJoin ? t('tribunal.plead') : isLive ? t('tribunal.observe') : t('tribunal.verdict')}
                <motion.span aria-hidden animate={{ x: [0, 3, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>→</motion.span>
              </span>
            </div>
          </motion.div>
        </Link>
      </TiltCard>
    </motion.div>
  );
}

// ── Compteur animé ─────────────────────────────────────────────────────────────

function Counter({ value, label, color }: { value: number; label: string; color: string }) {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 60, damping: 18 });
  const [display, setDisplay] = useState(0);

  useEffect(() => { mv.set(value); }, [value, mv]);
  useEffect(() => spring.on('change', (v) => setDisplay(Math.round(v))), [spring]);

  return (
    <div className="text-center">
      <motion.div className="text-3xl font-black tabular-nums" style={{ color }}>
        {display}
      </motion.div>
      <div className="text-[11px] uppercase tracking-widest mt-1 text-muted-foreground">{label}</div>
    </div>
  );
}

// ── Modal génération ──────────────────────────────────────────────────────────

const TYPE_OPTS = [
  { v: 'pénal',      icon: '⚔️', key: 'penal' },
  { v: 'civil',      icon: '📋', key: 'civil' },
  { v: 'commercial', icon: '💼', key: 'commercial' },
  { v: 'social',     icon: '🤝', key: 'social' },
];
const DIFF_OPTS = [
  { v: 'easy',   stars: '★☆☆', color: '#4f7a6a' },
  { v: 'medium', stars: '★★☆', color: '#a8742a' },
  { v: 'hard',   stars: '★★★', color: '#9b3a2e' },
];

const MAX_FILES      = 5;
const MAX_FILE_BYTES = 8 * 1024 * 1024;
const MAX_CONTEXT    = 4000;

/** Pièce déposée par l'utilisateur, envoyée à /api/trials/extract pour en lire le texte. */
interface Attachment {
  filename:   string;
  size:       number;
  status:     'reading' | 'ready' | 'error';
  text?:      string;
  chars?:     number;
  truncated?: boolean;
  error?:     string;
}

function GenerateModal({ onClose, onGenerated }: { onClose: () => void; onGenerated: (t: Trial) => void }) {
  const { t } = useTranslation();
  const [caseType,   setCaseType]   = useState('pénal');
  const [difficulty, setDifficulty] = useState('medium');
  const [context,    setContext]    = useState('');
  const [files,      setFiles]      = useState<Attachment[]>([]);
  const [dragging,   setDragging]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const totalChars = files.reduce((n, f) => n + (f.chars ?? 0), 0);

  /** Envoie chaque fichier à /api/trials/extract pour en tirer le texte. */
  const addFiles = useCallback(async (list: FileList | null) => {
    if (!list?.length) return;
    setError('');
    const room = MAX_FILES - files.length;
    if (room <= 0) { setError(t('tribunal.modal.errors.max_files', { max: MAX_FILES })); return; }

    const picked = Array.from(list).slice(0, room);
    setFiles(prev => [
      ...prev,
      ...picked.map(f => ({ filename: f.name, size: f.size, status: 'reading' as const })),
    ]);

    for (const f of picked) {
      try {
        if (f.size > MAX_FILE_BYTES) throw new Error(t('tribunal.modal.errors.too_large'));
        const fd = new FormData();
        fd.append('file', f);
        const token = await getIdToken().catch(() => null);
        const r = await fetch(withLang('/api/trials/extract'), {
          method: 'POST',
          body: fd,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.detail || t('tribunal.modal.errors.read_failed'));
        }
        const d = await r.json();
        setFiles(prev => prev.map(x =>
          x.filename === f.name && x.status === 'reading'
            ? { ...x, status: 'ready', text: d.text, chars: d.chars, truncated: d.truncated }
            : x));
      } catch (e: any) {
        setFiles(prev => prev.map(x =>
          x.filename === f.name && x.status === 'reading'
            ? { ...x, status: 'error', error: e.message || t('tribunal.modal.errors.generic') }
            : x));
      }
    }
  }, [files.length, t]);

  const removeFile = (name: string) => setFiles(prev => prev.filter(f => f.filename !== name));

  const busy    = files.some(f => f.status === 'reading');
  const grounded = context.trim().length > 0 || files.some(f => f.status === 'ready');

  const generate = async () => {
    setLoading(true); setError('');
    try {
      // Jeton Firebase s'il y en a un : la génération peut exiger un compte
      const token = await getIdToken().catch(() => null);
      const r = await fetch(withLang('/api/trials/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          case_type: caseType,
          difficulty,
          context: context.trim() || null,
          attachments: files
            .filter(f => f.status === 'ready')
            .map(f => ({ filename: f.filename, text: f.text ?? '', chars: f.chars ?? 0 })),
        }),
      });
      if (!r.ok) {
        // Message lisible plutôt que le JSON brut {"detail": …}
        const raw = await r.text();
        let msg = raw;
        try { const d = JSON.parse(raw).detail; msg = typeof d === 'string' ? d : t('tribunal.modal.errors.generic'); } catch { /* texte brut */ }
        throw new Error(msg);
      }
      onGenerated(await r.json());
      onClose();
    } catch (e: any) { setError(e.message || t('tribunal.modal.errors.generic')); }
    finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        style={{ background: 'rgba(7,14,23,0.45)', backdropFilter: 'blur(6px)' }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 10 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className="w-full max-w-lg rounded-3xl overflow-hidden my-8 bg-background"
          style={{
            border: '1px solid #e3d8c3',
            boxShadow: '0 24px 60px rgba(7,14,23,0.18)',
          }}
        >
          {/* Header */}
          <div
            className="relative px-8 py-6"
            style={{
              background: 'linear-gradient(135deg, #f6efe0 0%, #ffffff 70%)',
              borderBottom: '1px solid #e3d8c3',
            }}
          >
            <img
              src={ROLE_AVATARS.judge}
              alt={t('tribunal.roles.judge')}
              className="absolute -top-4 right-4 w-24 h-24 rounded-full bg-[#f3e8cc]"
              style={{ filter: 'drop-shadow(0 6px 16px rgba(168,116,42,0.25))' }}
            />
            <h2 className="font-serif text-2xl font-bold text-foreground mb-1">{t('tribunal.modal.title')}</h2>
            <p className="text-sm text-muted-foreground max-w-[75%]">
              {t('tribunal.modal.intro')}
            </p>
          </div>

          <div className="px-8 py-6 space-y-6">
            {/* Type */}
            <div>
              <label className="block text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-3">
                {t('tribunal.modal.case_type')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {TYPE_OPTS.map(({ v, icon, key }) => (
                  <motion.button
                    key={v}
                    onClick={() => setCaseType(v)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="text-left p-3 rounded-xl border transition-all duration-200"
                    style={{
                      background:  caseType === v ? '#f6efe0' : '#ffffff',
                      borderColor: caseType === v ? '#a8742a' : '#e3d8c3',
                    }}
                  >
                    <div className="text-xl mb-1">{icon}</div>
                    <div className="text-sm font-bold text-foreground">{t(`tribunal.case_types.${key}`)}</div>
                    <div className="text-[11px] text-muted-foreground">{t(`tribunal.case_types_desc.${key}`)}</div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Difficulté */}
            <div>
              <label className="block text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-3">
                {t('tribunal.modal.difficulty')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {DIFF_OPTS.map(({ v, stars, color }) => (
                  <motion.button
                    key={v}
                    onClick={() => setDifficulty(v)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="py-3 rounded-xl border text-center transition-all duration-200"
                    style={{
                      background:  difficulty === v ? `${color}12` : '#ffffff',
                      borderColor: difficulty === v ? color : '#e3d8c3',
                    }}
                  >
                    <div className="text-sm font-bold" style={{ color: difficulty === v ? color : '#6e5f4b' }}>{stars}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{t(`tribunal.difficulty.${v}`)}</div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Contexte libre */}
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <label className="text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground">
                  {t('tribunal.modal.context')} <span className="font-semibold normal-case tracking-normal">{t('tribunal.modal.optional')}</span>
                </label>
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {context.length}/{MAX_CONTEXT}
                </span>
              </div>
              <textarea
                value={context}
                onChange={e => setContext(e.target.value.slice(0, MAX_CONTEXT))}
                rows={4}
                placeholder={t('tribunal.modal.context_placeholder')}
                className="w-full rounded-xl border px-4 py-3 text-sm resize-none bg-white text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2"
                style={{ borderColor: '#e3d8c3', ...( { '--tw-ring-color': '#e9cf98' } as React.CSSProperties) }}
              />
            </div>

            {/* Pièces */}
            <div>
              <label className="block text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-3">
                {t('tribunal.modal.attachments')} <span className="font-semibold normal-case tracking-normal">{t('tribunal.modal.optional')}</span>
              </label>

              <motion.div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
                onClick={() => fileInput.current?.click()}
                animate={{ scale: dragging ? 1.01 : 1 }}
                className="rounded-xl border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors"
                style={{
                  borderColor: dragging ? '#a8742a' : '#e3d8c3',
                  background:  dragging ? '#f6efe0' : '#fbf6ea',
                }}
              >
                <div className="text-2xl mb-1.5">📎</div>
                <div className="text-sm font-semibold text-foreground">
                  {t('tribunal.modal.dropzone')}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {t('tribunal.modal.dropzone_hint', { max: MAX_FILES })}
                </div>
              </motion.div>

              <input
                ref={fileInput}
                type="file"
                multiple
                accept=".pdf,.docx,.doc,.txt,.md,.csv,.json"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />

              <AnimatePresence>
                {files.map(f => (
                  <motion.div
                    key={f.filename}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg text-xs"
                    style={{
                      background: f.status === 'error' ? '#f6e9e3' : '#ffffff',
                      border: `1px solid ${f.status === 'error' ? '#e2c3b5' : '#e3d8c3'}`,
                    }}
                  >
                    <span className="flex-shrink-0">
                      {f.status === 'reading' ? (
                        <motion.span
                          className="inline-block"
                          animate={{ rotate: 360 }}
                          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                        >⏳</motion.span>
                      ) : f.status === 'error' ? '⚠️' : '📄'}
                    </span>
                    <span className="font-semibold text-foreground truncate flex-1">{f.filename}</span>
                    {f.status === 'ready' && (
                      <span className="text-muted-foreground tabular-nums flex-shrink-0">
                        {t('tribunal.modal.chars', { chars: f.chars?.toLocaleString(getDateLocale()) })}{f.truncated ? ` ${t('tribunal.modal.truncated')}` : ''}
                      </span>
                    )}
                    {f.status === 'error' && (
                      <span className="text-[#9b3a2e] flex-shrink-0">{f.error}</span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFile(f.filename); }}
                      className="text-muted-foreground hover:text-foreground flex-shrink-0 px-1"
                      aria-label={t('tribunal.modal.remove', { name: f.filename })}
                    >
                      ✕
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>

              {grounded && (
                <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                  <Trans
                    i18nKey="tribunal.modal.grounded"
                    values={{ read: totalChars > 0 ? ` ${t('tribunal.modal.chars_read', { chars: totalChars.toLocaleString(getDateLocale()) })}` : '' }}
                    components={{ strong: <strong className="text-foreground" /> }}
                  />
                </p>
              )}
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-[#9b3a2e] text-sm px-4 py-3 rounded-xl"
                  style={{ background: '#f6e9e3', border: '1px solid #e2c3b5' }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Boutons */}
            <div className="flex gap-3">
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 py-3.5 rounded-xl text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                style={{ background: '#ffffff', border: '1px solid #e3d8c3' }}
              >
                {t('common.cancel')}
              </motion.button>
              <motion.button
                onClick={generate}
                disabled={loading || busy}
                whileHover={{ scale: loading || busy ? 1 : 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 py-3.5 rounded-xl text-white text-sm font-bold transition-all disabled:opacity-50"
                style={{ background: '#a8742a', boxShadow: '0 4px 16px rgba(168,116,42,0.25)' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.svg
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                      className="w-4 h-4"
                      fill="none" viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </motion.svg>
                    {t('tribunal.modal.generating')}
                  </span>
                ) : busy ? t('tribunal.modal.reading') : grounded ? t('tribunal.modal.generate_from_file') : t('tribunal.modal.generate_case')}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Page principale ────────────────────────────────────────────────────────────

const FILTER_STATUS = [
  { v: '',             l: 'tribunal.filters.all' },
  { v: 'open',         l: 'tribunal.filters.open' },
  { v: 'in_progress',  l: 'tribunal.filters.live' },
  { v: 'completed',    l: 'tribunal.filters.done' },
];
const FILTER_TYPE = [
  { v: '',            icon: '',   l: 'tribunal.filters.all_types' },
  { v: 'pénal',       icon: '⚔️', l: 'tribunal.case_types.penal' },
  { v: 'civil',       icon: '📋', l: 'tribunal.case_types.civil' },
  { v: 'commercial',  icon: '💼', l: 'tribunal.case_types.commercial' },
  { v: 'social',      icon: '🤝', l: 'tribunal.case_types.social' },
];

export default function TrialsHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [trials,       setTrials]       = useState<Trial[]>([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter,   setTypeFilter]   = useState('');
  const [showModal,    setShowModal]    = useState(false);

  // Seule la dernière requête compte : un clic rapide sur les filtres ne doit
  // pas laisser à l'écran les résultats du filtre précédent.
  const fetchSeq = useRef(0);
  const fetchTrials = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    const params = new URLSearchParams({ limit: '24', offset: '0' });
    if (statusFilter) params.set('status', statusFilter);
    if (typeFilter)   params.set('case_type', typeFilter);
    try {
      const r = await fetch(withLang(`/api/trials?${params}`));
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      if (seq !== fetchSeq.current) return;
      setTrials(d.trials ?? []);
      setTotal(d.total ?? 0);
    } catch { /* silencieux : la liste précédente reste affichée */ }
    finally { if (seq === fetchSeq.current) setLoading(false); }
  }, [statusFilter, typeFilter]);

  useEffect(() => { fetchTrials(); }, [fetchTrials]);

  // « Prendre un siège libre » : on filtre les affaires en attente et on y descend
  const showOpenSeats = () => {
    setStatusFilter('open');
    document.getElementById('affaires')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openCount = trials.filter(t => t.status === 'open').length;
  const liveCount = trials.filter(t => t.status === 'in_progress').length;
  const doneCount = trials.filter(t => t.status === 'completed').length;

  return (
    <MotionConfig reducedMotion="user">
      <Helmet>
        <title>{`${t('tribunal.title')} — ${t('tribunal.meta.title_suffix')}`}</title>
        <meta name="description" content={t('tribunal.meta.description')} />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IM+Fell+English:ital@0;1&display=swap" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background overflow-x-clip">
        <PublicHeader />

        <main className="flex-1">

          {/* ── HERO : une salle au hasard, qui parle, audiences en direct ── */}
          <CourtHero onSeat={showOpenSeats} onOpen={() => setShowModal(true)} />

          {/* ── COMPTEURS ────────────────────────────────────────────────── */}
          <div id="affaires" className="max-w-6xl mx-auto px-4 pt-10 scroll-mt-20">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-8 sm:gap-12 py-4 rounded-2xl"
              style={{ background: '#fbf6ea', border: '1px solid #e3d8c3' }}
            >
              <Counter value={openCount} label={t('tribunal.stats.open')}  color="#a8742a" />
              <div className="w-px h-9 bg-border" />
              <Counter value={liveCount} label={t('tribunal.stats.live')}  color="#9b3a2e" />
              <div className="w-px h-9 bg-border" />
              <Counter value={doneCount} label={t('tribunal.stats.done')}  color="#6e5f4b" />
              <div className="w-px h-9 bg-border" />
              <Counter value={total}     label={t('tribunal.stats.total')} color="#a8742a" />
            </motion.div>
          </div>

          {/* ── FILTRES + GRILLE ──────────────────────────────────────────── */}
          <div className="max-w-5xl mx-auto px-4 py-8">

            {/* Filtres */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex flex-wrap items-center gap-2 mb-8 p-4 rounded-2xl"
              style={{ background: '#fbf6ea', border: '1px solid #e3d8c3' }}
            >
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mr-1">{t('tribunal.filters.status')}</span>
              {FILTER_STATUS.map(({ v, l }) => (
                <motion.button
                  key={v}
                  onClick={() => setStatusFilter(v)}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  className="text-xs px-3 py-1.5 rounded-full font-semibold transition-all duration-200 relative"
                  style={{
                    background: statusFilter === v ? '#a8742a' : '#ffffff',
                    color: statusFilter === v ? '#fff' : '#6e5f4b',
                    border: `1px solid ${statusFilter === v ? '#a8742a' : '#e3d8c3'}`,
                    boxShadow: statusFilter === v ? '0 2px 8px rgba(168,116,42,0.25)' : 'none',
                  }}
                >
                  {t(l)}
                </motion.button>
              ))}

              <div className="w-px h-5 bg-border mx-1" />

              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mr-1">{t('tribunal.filters.type')}</span>
              {FILTER_TYPE.map(({ v, icon, l }) => (
                <motion.button
                  key={v}
                  onClick={() => setTypeFilter(v)}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  className="text-xs px-3 py-1.5 rounded-full font-semibold transition-all duration-200"
                  style={{
                    background: typeFilter === v ? '#3d5a73' : '#ffffff',
                    color: typeFilter === v ? '#fff' : '#6e5f4b',
                    border: `1px solid ${typeFilter === v ? '#3d5a73' : '#e3d8c3'}`,
                    boxShadow: typeFilter === v ? '0 2px 8px rgba(61,90,115,0.25)' : 'none',
                  }}
                >
                  {icon ? `${icon} ${t(l)}` : t(l)}
                </motion.button>
              ))}

              <motion.button
                onClick={fetchTrials}
                whileHover={{ rotate: 180, scale: 1.1 }}
                transition={{ duration: 0.3 }}
                className="ml-auto text-muted-foreground hover:text-foreground text-base"
                title={t('tribunal.filters.refresh')}
              >
                ↻
              </motion.button>
            </motion.div>

            {/* Grille */}
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div
                  key="skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{ opacity: [0.4, 0.7, 0.4] }}
                      transition={{ repeat: Infinity, duration: 1.5, delay: i * 0.1 }}
                      className="rounded-2xl p-5 h-52"
                      style={{ background: '#fbf6ea', border: '1px solid #e3d8c3' }}
                    />
                  ))}
                </motion.div>
              ) : trials.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-24"
                >
                  <motion.img
                    src={ROLE_AVATARS.judge}
                    alt={t('tribunal.roles.judge')}
                    className="w-28 h-28 mx-auto mb-6 opacity-50 rounded-full"
                    animate={{ y: [0, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 3 }}
                  />
                  <p className="text-muted-foreground mb-6 text-lg">{t('tribunal.list.empty')}</p>
                  <motion.button
                    onClick={() => setShowModal(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="inline-flex items-center gap-2 text-white font-bold px-6 py-3.5 rounded-xl text-sm"
                    style={{ background: '#a8742a', boxShadow: '0 4px 16px rgba(168,116,42,0.25)' }}
                  >
                    {t('tribunal.list.create_first')}
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  key="grid"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  <AnimatePresence>
                    {trials.map((t, i) => <TrialCard key={t.id} trial={t} index={i} />)}
                  </AnimatePresence>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="col-span-full text-center text-xs text-muted-foreground mt-4"
                  >
                    {t('tribunal.list.count', { count: trials.length, total })}
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── LE GRAND PROCÈS, DANS SA SALLE ───────────────────────────── */}
          <FamousTrial />

          {/* ── LE VIBE LEGAL ─────────────────────────────────────────────── */}
          <VibeLegal />

          {/* ── COMMENT ÇA MARCHE ─────────────────────────────────────────── */}
          <div
            id="comment-ca-marche"
            className="border-t"
            style={{ borderColor: '#e3d8c3', background: '#fbf6ea' }}
          >
            <div className="max-w-5xl mx-auto px-4 py-14">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-10"
              >
                <h2 className="font-serif text-3xl font-bold text-foreground mb-2">{t('tribunal.how.title')}</h2>
                <p className="text-muted-foreground text-sm">{t('tribunal.how.subtitle')}</p>
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-px overflow-hidden rounded-2xl" style={{ background: '#e3d8c3', border: '1px solid #e3d8c3' }}>
                {[
                  { avatar: ROLE_AVATARS.clerk,       step: '01', title: t('tribunal.how.step1_title'), desc: t('tribunal.how.step1_desc'), color: '#a8742a', delay: 0 },
                  { avatar: ROLE_AVATARS.prosecution, step: '02', title: t('tribunal.how.step2_title'), desc: t('tribunal.how.step2_desc'), color: '#9b3a2e', delay: 0.1 },
                  { avatar: ROLE_AVATARS.witness,     step: '03', title: t('tribunal.how.step3_title'), desc: t('tribunal.how.step3_desc'), color: '#4f7a6a', delay: 0.2 },
                  { avatar: ROLE_AVATARS.judge,       step: '04', title: t('tribunal.how.step4_title'), desc: t('tribunal.how.step4_desc'), color: '#3d5a73', delay: 0.3 },
                ].map(({ avatar, step, title, desc, color, delay }) => (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay, duration: 0.5 }}
                    whileHover={{ y: -4, background: '#fbf6ea' }}
                    className="relative p-6 text-center cursor-default transition-colors"
                    style={{ background: '#ffffff' }}
                  >
                    <div className="relative inline-block mb-4">
                      <motion.img
                        src={avatar}
                        alt={title}
                        className="w-20 h-20 mx-auto rounded-full bg-[#f3e8cc]"
                        whileHover={{ scale: 1.06 }}
                        style={{ filter: `drop-shadow(0 4px 10px ${color}33)` }}
                      />
                      <span
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white"
                        style={{ background: color }}
                      >
                        {step}
                      </span>
                    </div>
                    <h3 className="font-bold text-foreground text-sm mb-2">{title}</h3>
                    <p className="text-xs leading-relaxed text-muted-foreground">{desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

        </main>

        <PublicFooter />
      </div>

      <AnimatePresence>
        {showModal && (
          <GenerateModal onClose={() => setShowModal(false)} onGenerated={(t) => navigate(`/tribunal/${t.id}`)} />
        )}
      </AnimatePresence>
    </MotionConfig>
  );
}
